// File: src/pages/host/HostDocsGatePage.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LuFileText, LuLogOut } from 'react-icons/lu';
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';
import { AuthContext } from '../../context/AuthContext';

// Toggle: you said you’re not doing background checks right now.
const REQUIRE_BG_AUTH = false;

/* ======== DOC CONTENT ======== */

const HOST_TERMS = `TRIBE PLATFORM TERMS OF SERVICE
(HOST VERSION)

These Platform Terms of Service (“Host Terms”) govern your access to and use of the Tribe platform as a host. These Host Terms are incorporated by reference into the Tribe Platform Access & Independent Contractor Agreement (the “Master Agreement”). Capitalized terms not defined here have the meanings given in the Master Agreement.

By accessing or using the platform as a host, you agree to these Host Terms.

1. PLATFORM ACCESS

Tribe provides hosts with access to software, tools, training, systems, and services to facilitate Tribe-approved events. Platform access is a privilege, not a right.

Tribe may approve, deny, limit, suspend, or revoke host access at any time, with or without notice, and in Tribe’s sole discretion, except where advance notice or procedural protections are expressly required by applicable law.

2. EVENT CREATION, APPROVAL, AND VISIBILITY

All events must be created, approved, and run through the Tribe platform.

Tribe retains sole discretion over:
- Event approval or denial
- Event placement, promotion, and visibility
- Event edits, cancellation, or removal
- Whether an event remains live on the platform

Tribe does not guarantee that any event will be approved, promoted, discovered, or attended.

3. HOST RELIABILITY AND PERFORMANCE

Tribe relies on hosts to run high-quality, in-person events. While Tribe does not guarantee hosts any minimum number of events or earnings, hosts are expected to act professionally and reliably when choosing to host events.

Without limiting any other rights Tribe may have, Tribe may take action if a host:
- Fails to show up to an event they agreed to host
- Cancels events without reasonable notice
- Repeatedly reschedules, abandons, or materially alters events
- Runs events that materially differ from their approved description
- Fails to follow Tribe’s prescribed event formats, training, or standards
- Demonstrates a pattern of unreliability, poor follow-through, or conduct inconsistent with platform expectations

Such action may include, without limitation:
- Removal of events
- Adjustment, withholding, or forfeiture of payouts
- Temporary suspension of hosting privileges
- Permanent removal from the platform

All determinations are made in Tribe’s sole discretion, subject only to non-waivable rights under applicable law.

4. INACTIVITY

Tribe may remove or deactivate host accounts that are inactive for an extended period of time or that demonstrate a lack of participation, follow-through, or engagement.

There is no guaranteed right to remain an active host.

5. PAYMENTS, REFUNDS, AND ADJUSTMENTS

Host compensation is governed by the Master Agreement.

Tribe may:
- Issue refunds to attendees at its discretion
- Adjust host payouts for refunds, chargebacks, disputes, errors, or policy violations
- Delay or withhold payments during investigations of fraud, misconduct, or platform misuse

Where required by applicable law, Tribe will provide any mandatory accounting or explanation related to withheld payments.

6. PLATFORM USE RESTRICTIONS

Hosts may not:
- Use the platform for non-Tribe or competing events
- Circumvent Tribe’s payment systems
- Collect attendee information outside approved platform workflows
- Move events, guests, or communications off-platform without approval
- Scrape, copy, reverse engineer, or misuse platform systems
- Use the platform in a manner that undermines safety, trust, or platform integrity

7. PLATFORM DISCRETION AND COMMUNITY PROTECTION

Tribe may suspend or terminate host access if Tribe determines, in its sole discretion, that:
- The host’s conduct violates these Host Terms, the Master Agreement, or incorporated policies
- The host poses a safety, reputational, operational, or legal risk
- The host’s behavior undermines the quality, trust, or integrity of the community
- Removal is in the best interest of Tribe, its users, or the platform

Except where required by applicable law:
- Tribe is not required to provide advance notice
- Tribe is not required to provide an opportunity to cure
- Tribe is not required to disclose internal enforcement criteria

8. NO RESPONSIBILITY FOR OFFLINE CONDUCT

Events facilitated through the platform involve real-world, in-person interactions.

Tribe does not:
- Supervise or control events in real time
- Verify the conduct of hosts, guests, or venues
- Act as a party to any offline arrangements

Hosts are solely responsible for:
- Their events
- Their conduct
- Their interactions with guests
- Compliance with applicable laws and venue rules

Nothing in these Host Terms limits rights that cannot be waived under applicable state law.

9. PLATFORM AVAILABILITY AND CHANGES

Tribe does not guarantee that the platform will be available, uninterrupted, or error-free.

Tribe may modify, suspend, or discontinue any part of the platform, including features, tools, event formats, host requirements, or compensation structures, at any time and without liability, except where prohibited by applicable law.

10. NO GUARANTEES

Tribe makes no guarantees regarding:
- Event approval
- Event promotion or visibility
- Attendance levels
- Earnings or payouts
- Continued access to the platform

Hosting through Tribe is undertaken at the host’s own risk.

11. STATE-SPECIFIC RIGHTS AND LIMITATIONS

Certain states provide hosts with non-waivable rights related to:
- Payment timing or wage statements
- Termination procedures
- Arbitration or class action waivers
- Notice or appeal requirements

Nothing in these Host Terms is intended to waive, limit, or circumvent rights that cannot be waived under applicable state law. Where a provision is deemed unenforceable in a particular jurisdiction, that provision shall apply only to the minimum extent required by law, and all remaining provisions shall remain in full force and effect.

12. UPDATES TO THESE TERMS

Tribe may update these Host Terms from time to time. Continued use of the platform after updates take effect constitutes acceptance of the revised terms, except where applicable law requires affirmative consent.

13. INCORPORATION BY REFERENCE

These Host Terms are incorporated into and governed by the Master Agreement, along with the Host Code of Conduct, Community Guidelines, and Privacy Policy.

If there is a conflict between these Host Terms and the Master Agreement, the Master Agreement controls.
`;

const MASTER_AGREEMENT = `TRIBE PLATFORM ACCESS & INDEPENDENT CONTRACTOR AGREEMENT
(MASTER AGREEMENT)

This Platform Access & Independent Contractor Agreement (“Agreement”) is entered into by and between Tribe, Inc. (“Tribe,” “Company,” “we,” “us”) and the individual or entity accepting this Agreement (“Host,” “you”).

By clicking “I Agree,” you acknowledge that you have read, understand, and agree to be legally bound by this Agreement.

1. RELATIONSHIP OF THE PARTIES
1.1 Independent Contractor Status
Host is an independent contractor and not an employee, agent, partner, joint venturer, franchisee, or representative of Tribe. Nothing in this Agreement creates or is intended to create an employment relationship.
1.2 No Employment Rights
Host is not entitled to wages, salary, overtime, benefits, workers’ compensation, unemployment insurance, sick leave, or any other employee protections.
1.3 No Authority
Host has no authority to bind Tribe or act on Tribe’s behalf for any purpose.
1.4 No Guaranteed Work or Income
Tribe does not guarantee Host any minimum number of events, attendees, bookings, visibility, or earnings.
1.5 Independent Business Representation
Host represents and warrants that they are customarily engaged in an independently established trade, occupation, or business of hosting, facilitation, or event-related services, and that they may provide similar services for other platforms or clients, subject only to the non-solicitation and confidentiality obligations set forth in this Agreement.

2. PLATFORM ACCESS & GOVERNANCE
2.1 License to Use Platform
Tribe grants Host a limited, revocable, non-exclusive, non-transferable, non-sublicensable license to access and use Tribe’s platform, tools, training materials, systems, and documentation (collectively, the “Platform”) solely to host Tribe-approved events.
2.2 Platform Control
Tribe retains sole discretion over, including but not limited to:
 • Event formats, structures, scripts, and standards
 • Training requirements and certification
 • Host eligibility and approval
 • Event approval, visibility, promotion, and removal
 • Suspension or termination of Platform access
2.3 Platform Modification or Discontinuation
Tribe may modify, suspend, or discontinue any part of the Platform, features, event models, or compensation structures at any time, without liability to Host.
2.4 No Ownership
Host acquires no ownership interest in the Platform, Tribe systems, training materials, or user base.

3. HOST ROLE & RESPONSIBILITIES
3.1 Prescribed Event Model
Host agrees to execute events strictly in accordance with Tribe’s prescribed formats, facilitation frameworks, scripts, training materials, and platform standards. Host may not modify or create derivative event models without Tribe’s prior written approval.
3.2 Choice to Participate
Host chooses whether and when to host events and is not required to accept any opportunity.
3.3 Compliance
Host is solely responsible for complying with all applicable laws, venue rules, permits, and Tribe policies.

4. COMPENSATION
4.1 Payment Structure
Compensation is per-event or per-ticket, as communicated through the Platform. Compensation is not hourly or salaried.
4.2 Adjustments
Tribe may withhold, adjust, or claw back payments for refunds, chargebacks, fraud, errors, or violations of this Agreement or incorporated policies.
4.3 Expenses
Host bears all expenses unless Tribe expressly agrees otherwise in writing.

5. TAXES
Host is solely responsible for all federal, state, and local taxes, filings, and reporting obligations arising from compensation received under this Agreement.

6. CONFIDENTIALITY & PROPRIETARY INFORMATION
Host will receive confidential and proprietary information, including but not limited to:
 • Training materials and coursework
 • Event formats, facilitation methods, scripts, and frameworks
 • Host manuals and internal documentation
 • Platform mechanics and workflows
 • User, guest, and host information
Host may not copy, disclose, sell, reuse, license, share, reverse engineer, or otherwise exploit such information outside Tribe. These obligations survive termination indefinitely.

7. NON-SOLICITATION & PLATFORM PROTECTION
During the term of this Agreement and for twelve (12) months following termination, Host shall not:
 • Solicit Tribe users, guests, attendees, or hosts
 • Move Tribe participants off the Platform
 • Build or promote competing platforms or communities using Tribe access
 • Represent or imply affiliation with Tribe after termination
This Agreement does not prohibit Host from engaging in hosting, facilitation, or event-based work generally, provided Host does not misuse Tribe’s confidential information or solicit Tribe users.
This section is intended to be narrowly tailored to protect Tribe’s legitimate business interests and shall be enforced to the maximum extent permitted by applicable law. Where restricted by law, this section shall be construed to apply only to the extent legally permissible.

8. INTELLECTUAL PROPERTY & CONTENT OWNERSHIP
8.1 Tribe Ownership of Host Content
All content created, submitted, or generated by Host in connection with Tribe, including but not limited to:
 • Event descriptions
 • Event names and formats
 • Photos, videos, recordings, and media
 • Written materials, prompts, scripts, or facilitation content
 • Feedback, suggestions, or improvements
shall be the exclusive property of Tribe worldwide and are deemed works made for hire to the maximum extent permitted by law.
8.2 Assignment
To the extent any such content is not deemed a work made for hire, Host irrevocably assigns all right, title, and interest in such content to Tribe, worldwide and in perpetuity.
8.3 Moral Rights Waiver
Host waives all moral rights and rights of attribution to the fullest extent permitted by law.
8.4 Excluded IP
Host retains ownership of intellectual property created entirely independently of Tribe and unrelated to Tribe events or training.

9. CODE OF CONDUCT & POLICIES
Host agrees to comply with Tribe’s Host Code of Conduct, Community Guidelines, and Platform Terms of Service (Host Version), each incorporated by reference and subject to update. Tribe may remove Host or events immediately for violations or conduct Tribe deems harmful, unsafe, or inconsistent with platform standards, in Tribe’s sole discretion.

10. EVENT RESPONSIBILITY, ASSUMPTION OF RISK & RELEASE
10.1 Event Responsibility
Host is solely responsible for event execution and on-site conduct. Tribe does not supervise or control events in real time.
10.2 Assumption of Risk
Host acknowledges that hosting in-person events involves inherent risks, including illness, injury, property damage, or misconduct by third parties, and voluntarily assumes all such risks.
10.3 Release of Claims
To the fullest extent permitted by law, Host releases Tribe from any claims arising out of or related to hosting or participating in events, except where prohibited by law.

11. PRIVACY & DATA
Host agrees to Tribe’s Privacy Policy regarding the collection, use, storage, and deletion of data.

12. BACKGROUND CHECKS (IF APPLICABLE)
If Tribe elects to conduct background checks, Host authorizes such checks in compliance with applicable law.

13. INSURANCE
Host is responsible for maintaining any insurance required by applicable law or venue requirements. Tribe may require proof of insurance for certain events, depending on size, location, or risk profile.

14. INDEMNIFICATION
Host agrees to indemnify, defend, and hold harmless Tribe from all claims, damages, losses, liabilities, costs, and expenses arising from:
 • Host’s events or conduct
 • Breach of this Agreement
 • Violation of law or third-party rights

15. DISCLAIMERS
THE PLATFORM IS PROVIDED “AS IS” AND “AS AVAILABLE.” TRIBE MAKES NO WARRANTIES, INCLUDING ANY GUARANTEE OF INCOME, ATTENDANCE, OR PLATFORM AVAILABILITY.

16. LIMITATION OF LIABILITY
TO THE MAXIMUM EXTENT PERMITTED BY LAW, TRIBE SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.

17. TIME LIMIT ON CLAIMS
Any claim arising out of or related to this Agreement or hosting activities must be brought within one (1) year of the event giving rise to the claim, to the extent permitted by law.

18. TERM & TERMINATION
Either party may terminate this Agreement at any time. Sections relating to confidentiality, intellectual property, non-solicitation, indemnification, releases, dispute resolution, and limitations of liability survive termination.

19. DISPUTE RESOLUTION & ARBITRATION
Except where prohibited by applicable law, any dispute arising out of or relating to this Agreement shall be resolved through binding individual arbitration. Class, collective, and representative actions are waived to the fullest extent permitted by law.
Where a state prohibits mandatory arbitration or class waivers for a specific claim, such claim shall proceed only to the minimum extent required by law, and all remaining provisions shall remain in full force and effect.

20. GOVERNING LAW & STATE-SPECIFIC CARVE-OUTS
This Agreement is governed by the laws of the state in which Host primarily performs services, without regard to conflict-of-law principles.
Notwithstanding the foregoing:
 • Nothing in this Agreement is intended to restrict rights that cannot be waived under applicable state law.
 • Any provision found unenforceable in a particular state shall be severed or limited solely as required by that state’s law.
 • All remaining provisions shall continue in full force and effect nationwide.

21. SEVERABILITY, ASSIGNMENT & ENTIRE AGREEMENT
21.1 Severability
If any provision is held invalid or unenforceable, it shall be severed or limited to the minimum extent necessary, and the remainder of the Agreement shall remain enforceable.
21.2 Assignment
Tribe may freely assign this Agreement. Host may not assign without Tribe’s prior written consent.
21.3 Entire Agreement
This Agreement constitutes the entire agreement between the parties and supersedes all prior agreements or understandings.

ACCEPTANCE
By clicking “I Agree,” Host confirms acceptance of this Agreement.
`;

const PRIVACY_POLICY = `TRIBE PRIVACY POLICY
(HOSTS)

Effective Date: January 11, 2026

This Privacy Policy explains how Tribe, Inc. (“Tribe,” “we,” “us,” or “our”) collects, uses, discloses, and retains information from individuals who apply to, onboard as, or act as hosts on the Tribe platform (“Hosts,” “you”).

This Privacy Policy is incorporated by reference into the Tribe Platform Access & Independent Contractor Agreement and the Tribe Platform Terms of Service (Host Version).

By accessing or using the platform as a host, you acknowledge and agree to the practices described in this Privacy Policy.

---

1. INFORMATION WE COLLECT

We may collect, store, and process the following categories of information:

### 1.1 Information You Provide Directly
- Full name
- Email address
- Phone number
- Date of birth (for eligibility and venue requirements)
- Address or general location
- Application responses
- Training responses, assessments, and certifications
- Communications with Tribe (email, chat, support requests)
- Event-related submissions (descriptions, schedules, notes)

### 1.2 Platform & Usage Information
- Event hosting activity
- Attendance and performance metrics
- Reliability indicators (e.g., cancellations, no-shows)
- Interaction with platform features
- Logs, timestamps, and device information
- IP address and approximate location data

### 1.3 Payment & Financial Information
- Payment account identifiers (processed via third-party providers)
- Transaction records
- Payout history
- Refunds, chargebacks, or disputes

Tribe does **not** store full bank account or payment card numbers directly.

### 1.4 Information from Third Parties
- Identity or eligibility verification (if used)
- Background check results (if applicable)
- Payment processors
- Analytics or fraud-prevention services
- Law enforcement or regulatory bodies (where legally required)

---

2. HOW WE USE INFORMATION

We may use host information for any lawful business purpose, including but not limited to:

- Evaluating host eligibility and onboarding
- Operating, maintaining, and improving the platform
- Administering training and certifications
- Approving, monitoring, and managing events
- Processing payments, refunds, and chargebacks
- Monitoring performance, reliability, and compliance
- Enforcing platform rules, policies, and agreements
- Ensuring safety, trust, and quality
- Communicating with hosts
- Preventing fraud, abuse, or misuse
- Legal compliance, audits, and dispute resolution
- Business analytics, research, and optimization
- Platform promotion and internal reporting

We may use aggregated or anonymized data for any purpose.

---

3. DISCLOSURE OF INFORMATION

We may disclose host information to:

### 3.1 Service Providers
Third parties that perform services on our behalf, including:
- Payment processors
- Hosting and infrastructure providers
- Analytics providers
- Customer support tools
- Security and fraud prevention vendors

### 3.2 Legal and Safety Disclosures
We may disclose information if we determine, in our discretion, that disclosure is:
- Required by law, subpoena, or legal process
- Necessary to protect the rights, safety, or property of Tribe, users, hosts, or the public
- Related to investigations of fraud, abuse, or misconduct

### 3.3 Business Transfers
In connection with a merger, acquisition, financing, reorganization, or sale of assets, host information may be transferred as part of the transaction.

---

4. DATA RETENTION

We retain host information for as long as reasonably necessary to:
- Operate the platform
- Enforce agreements and policies
- Comply with legal obligations
- Resolve disputes
- Maintain business records

We may retain certain information after account deactivation where legally required or reasonably necessary for legitimate business purposes.

---

5. DATA SECURITY

We use reasonable administrative, technical, and organizational measures designed to protect information. However, no system is completely secure, and we cannot guarantee absolute security.

---

6. YOUR RIGHTS AND CHOICES

Depending on your location, you may have certain rights regarding your personal information, including:
- Access to information we hold about you
- Correction of inaccurate information
- Deletion of information (subject to legal and operational limitations)

Requests may be submitted to: **[privacy@jointribe.io]**

We may require verification before processing requests.

---

7. STATE-SPECIFIC DISCLOSURES

Certain U.S. states provide additional privacy rights, including but not limited to California, Virginia, Colorado, Connecticut, and Utah.

Where required by applicable law:
- We will honor non-waivable rights
- We will provide required disclosures
- We will limit or modify data practices only to the extent required

Nothing in this Privacy Policy is intended to waive rights that cannot be waived under applicable law.

---

8. NO SALE OF PERSONAL INFORMATION

Tribe does not sell personal information as defined by applicable law. However, certain data sharing practices may be considered a “sale” or “sharing” under specific state laws. Where applicable, hosts may exercise opt-out rights as required by law.

---

9. CHILDREN

The platform is not intended for individuals under 18. We do not knowingly collect information from minors.

---

10. CHANGES TO THIS POLICY

We may update this Privacy Policy at any time. Updates will be posted with a revised effective date. Continued use of the platform after changes constitutes acceptance, except where affirmative consent is required by law.

---

11. CONTACT

For questions or requests regarding this Privacy Policy, contact:

privacy@jointribe.io
`;

const BG_DISCLOSURE = `BACKGROUND CHECK DISCLOSURE & AUTHORIZATION

Effective Date: January 11, 2026

Tribe, Inc. (“Tribe”) requires background checks for individuals applying to or acting as hosts on the Tribe platform, where permitted by law. These background checks are conducted for eligibility, safety, and platform integrity purposes.

DISCLOSURE

Tribe may obtain one or more consumer reports about you, including criminal history reports and other background information, from a third-party consumer reporting agency. These reports may be obtained at the time of application and, if approved, periodically during your participation as a host, subject to applicable law.

Hosts act as independent contractors, not employees. Background checks are used solely to determine eligibility to access the platform and host events.

ADDITIONAL NOTICES

- Background checks are conducted through a third-party provider.
- Tribe may deny, suspend, or revoke host access based on background check results, where permitted by law.
- Tribe will comply with all applicable laws governing background checks, including required notices and procedures.

AUTHORIZATION (REQUIRED)

By clicking “I Authorize,” you acknowledge that you have read and understand this disclosure and authorize Tribe to obtain consumer reports and conduct background checks about you, including criminal history reports, for the purposes described above.

Authorization is required to proceed with host onboarding. If you do not authorize a background check, you will not be eligible to host events on the Tribe platform.

☐ I authorize Tribe to conduct background checks and obtain consumer reports for eligibility, safety, and platform integrity purposes.

By clicking “I Authorize,” you provide your electronic signature and consent.
`;

function hasScrolledToBottom(el) {
  if (!el) return false;
  const slack = 12;
  return el.scrollTop + el.clientHeight >= el.scrollHeight - slack;
}

function isAllCapsHeading(line) {
  const t = String(line || '').trim();
  if (!t) return false;
  const letters = t.replace(/[^A-Za-z]/g, '');
  if (letters.length < 4) return false;
  return letters === letters.toUpperCase() && t.length <= 60;
}

function parseDocText(text) {
  const lines = String(text || '').replace(/\r/g, '').split('\n');

  const firstNonEmpty = lines.findIndex(l => String(l || '').trim().length > 0);

  return lines.map((raw, i) => {
    const line = String(raw || '');
    const t = line.trim();

    if (!t) return { type: 'spacer', text: '' };

    if (i === firstNonEmpty) return { type: 'title', text: t };

    if (i === firstNonEmpty + 1 && /^\(.*\)$/.test(t)) return { type: 'subtitle', text: t };

    if (/^Effective Date:/i.test(t)) return { type: 'meta', text: t };

    // e.g. "1. PLATFORM ACCESS" or "1.1 Something" or "10–21. Additional..."
    if (
      /^\d+(\.\d+)?\./.test(t) ||
      /^\d+(\.\d+)?\s+[A-Z]/.test(t) ||
      /^\d+[\-–]\d+\./.test(t)
    ) {
      return { type: 'heading', text: t };
    }

    if (isAllCapsHeading(t)) return { type: 'heading', text: t };

    if (t.startsWith('- ')) return { type: 'bullet', text: t.slice(2) };
    if (t.startsWith('• ')) return { type: 'bullet', text: t.slice(2) };
    if (t.startsWith('☐')) return { type: 'check', text: t };

    return { type: 'para', text: t };
  });
}

export default function HostDocsGatePage({ onAccepted, onNotHost, onRequired, required = false } = {}) {
  const navigate = useNavigate();
  const auth = useContext(AuthContext) || {};

  const [requiredVersion, setRequiredVersion] = useState('');
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState('');
  const [statusAttempt, setStatusAttempt] = useState(0);

  // Wizard state
  const [step, setStep] = useState(0);

  const [ack, setAck] = useState({
    hostTerms: false,
    masterAgreement: false,
    privacyPolicy: false,
    backgroundCheckAuth: false,
  });

  const [scrolled, setScrolled] = useState({
    hostTerms: false,
    masterAgreement: false,
    privacyPolicy: false,
    backgroundCheckAuth: false,
  });

  const [signatureName, setSignatureName] = useState('');
  const [finalConfirm, setFinalConfirm] = useState(false);

  const [saving, setSaving] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [err, setErr] = useState('');

  const termsRef = useRef(null);
  const masterRef = useRef(null);
  const privacyRef = useRef(null);
  const bgRef = useRef(null);

  const steps = useMemo(() => {
    const base = [
      { key: 'hostTerms', title: 'Terms of Service', text: HOST_TERMS, ref: termsRef },
      { key: 'masterAgreement', title: 'Master Agreement', text: MASTER_AGREEMENT, ref: masterRef },
      { key: 'privacyPolicy', title: 'Privacy Policy', text: PRIVACY_POLICY, ref: privacyRef },
    ];

    if (REQUIRE_BG_AUTH) {
      base.push({ key: 'backgroundCheckAuth', title: 'Background Check Authorization', text: BG_DISCLOSURE, ref: bgRef });
    }

    base.push({ key: 'signature', title: 'Sign & Accept', text: '', ref: null });

    return base;
  }, []);

  const current = steps[step];
  const isSignatureStep = current?.key === 'signature';

  const progressText = useMemo(() => {
    const n = steps.length || 1;
    const idx = Math.min(Math.max(step + 1, 1), n);
    return { idx, n };
  }, [step, steps.length]);

  const headerTitle = current?.title || 'Agreements';

  const canContinueDocStep = () => {
    const k = current?.key;
    if (!k || k === 'signature') return false;
    return !!scrolled[k] && !!ack[k];
  };

  const canFinish = () => {
    if (!ack.hostTerms || !ack.masterAgreement || !ack.privacyPolicy) return false;
    if (REQUIRE_BG_AUTH && !ack.backgroundCheckAuth) return false;
    if (String(signatureName).trim().length < 3) return false;
    if (!finalConfirm) return false;
    return true;
  };

  const footerPrimaryDisabled = saving || loggingOut || loadingStatus || (!isSignatureStep ? !canContinueDocStep() : !canFinish());
  const footerBackDisabled = saving || loggingOut || loadingStatus || step === 0;

  const docRows = useMemo(() => parseDocText(current?.text || ''), [current?.text]);

  function onScrollFor(key, ref) {
    const el = ref?.current;
    if (!el) return;
    if (hasScrolledToBottom(el)) {
      setScrolled(prev => ({ ...prev, [key]: true }));
    }
  }

  // Load required version and redirect if already accepted
  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoadingStatus(true);
      setStatusError('');
      try {
        const res = await axios.get('/host/docs/status');
        if (!mounted) return;

        if (typeof res.data?.isHost !== 'boolean' || typeof res.data?.accepted !== 'boolean') {
          throw new Error('Unable to verify host agreement status.');
        }
        if (!res.data.isHost) {
          if (onNotHost) onNotHost();
          else navigate('/events', { replace: true });
          return;
        }
        if (res.data.accepted) {
          if (onAccepted) onAccepted();
          else navigate('/host', { replace: true });
          return;
        }

        setRequiredVersion(String(res.data.requiredVersion || ''));
        if (onRequired) onRequired();
      } catch (e) {
        if (mounted) {
          setStatusError('Unable to load your host agreements. Please try again.');
          if (onRequired) onRequired();
        }
      } finally {
        if (mounted) setLoadingStatus(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [navigate, onAccepted, onNotHost, onRequired, statusAttempt]);

  // Optional: scroll doc to top when step changes
  useEffect(() => {
    if (!current?.ref?.current) return;
    try {
      current.ref.current.scrollTop = 0;
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  async function acceptAll() {
    if (saving || loggingOut || loadingStatus || !canFinish()) return;
    setSaving(true);
    setErr('');
    try {
      await axios.post('/host/docs/accept', {
        signatureName: String(signatureName || '').trim(),
        acceptedDocs: {
          hostTerms: ack.hostTerms,
          masterAgreement: ack.masterAgreement,
          privacyPolicy: ack.privacyPolicy,
          backgroundCheckAuth: REQUIRE_BG_AUTH ? ack.backgroundCheckAuth : false,
        },
      });

      if (onAccepted) onAccepted();
      else navigate('/host', { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Failed to accept documents');
    } finally {
      setSaving(false);
    }
  }

  async function handleLogout() {
    if (saving || loggingOut) return;
    setLoggingOut(true);
    try {
      if (typeof auth.logout === 'function') await auth.logout();
    } catch {
      // Always clear this browser's session even if remote logout fails.
    } finally {
      for (const key of ['accessToken', 'token', 'authToken', 'refreshToken', 'sessionId', 'userId', 'userName', 'jwt', 'tribe_auth']) {
        try { localStorage.removeItem(key); } catch {}
      }
      for (const key of ['tribe_mobile_host_docs_finished', 'tribe_mobile_host_docs_cleared_v2', 'tribe_mobile_host_docs_required_v2', 'tribe_portal_access_handoff']) {
        try { sessionStorage.removeItem(key); } catch {}
      }
      window.location.replace('/login');
    }
  }

  // The layout stays visible while this background check is pending.
  if (loadingStatus && !required) return null;

  if (statusError) {
    return (
      <main style={{ ...UI.page, display: 'grid', placeItems: 'center', padding: 24, boxSizing: 'border-box' }}>
        <div style={{ maxWidth: 340, textAlign: 'center' }}>
          <p role="alert" style={{ color: '#777', fontSize: 14, lineHeight: 1.6 }}>
            {statusError}
          </p>
          {statusError && (
            <button type="button" style={{ ...UI.footerBtn, ...UI.footerBtnPrimary }} onClick={() => setStatusAttempt(attempt => attempt + 1)}>
              Try again
            </button>
          )}
          <button type="button" disabled={loggingOut} style={{ ...UI.footerBtn, ...UI.footerBtnGhost, marginLeft: 10 }} onClick={handleLogout}>
            {loggingOut ? 'Logging out…' : 'Log out'}
          </button>
        </div>
      </main>
    );
  }

  return (
    <div style={UI.page}>
      <header style={UI.headerContainer}>
        <div style={UI.headerLogoRow}>
          <TribeLogo style={UI.logoSvg} />
        </div>
        <div style={UI.headerNavRow}>
          <button type="button" onClick={handleLogout} disabled={saving || loggingOut} style={UI.logoutButton}>
            <LuLogOut size={13} aria-hidden="true" />
            {loggingOut ? 'Logging out…' : 'Logout'}
          </button>
          <div style={UI.pageTitle}>{headerTitle}</div>
          <div style={UI.progressContainer}>
            <div style={UI.stepCounter} aria-label={`Step ${progressText.idx} of ${progressText.n}`}>
              {progressText.idx} of {progressText.n}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div style={UI.main}>
        {/* New hero (icon + heading + short subheading) */}
        <div style={UI.hero}>
          <div style={UI.heroIconBox} aria-hidden="true">
            <LuFileText size={22} color="#0b0b0b" />
          </div>

          <div style={UI.heroTitle}>Host agreements</div>
          <div style={UI.heroSub}>
            Read and accept each document to continue to Tribe.
          </div>

          {(requiredVersion || loadingStatus) ? (
            <div style={UI.heroMetaRow}>
              {requiredVersion ? (
                <div style={UI.heroChip}>
                  Version <span style={{ fontWeight: 900 }}>{requiredVersion}</span>
                </div>
              ) : null}

              {loadingStatus ? (
                <div style={UI.heroChipMuted}>Loading…</div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div style={UI.panel}>
          {!isSignatureStep ? (
            <>
              <div
                ref={current.ref}
                onScroll={() => onScrollFor(current.key, current.ref)}
                style={UI.docBox}
              >
                <div style={UI.docInner}>
                  {docRows.map((row, i) => {
                    if (row.type === 'spacer') return <div key={i} style={UI.docSpacer} />;

                    if (row.type === 'title') {
                      return <div key={i} style={UI.docTitle}>{row.text}</div>;
                    }

                    if (row.type === 'subtitle') {
                      return <div key={i} style={UI.docSubtitle}>{row.text}</div>;
                    }

                    if (row.type === 'meta') {
                      return <div key={i} style={UI.docMeta}>{row.text}</div>;
                    }

                    if (row.type === 'heading') {
                      return <div key={i} style={UI.docHeading}>{row.text}</div>;
                    }

                    if (row.type === 'bullet') {
                      return (
                        <div key={i} style={UI.docBulletRow}>
                          <span style={UI.docBulletDot}>•</span>
                          <span style={UI.docBulletText}>{row.text}</span>
                        </div>
                      );
                    }

                    if (row.type === 'check') {
                      return <div key={i} style={UI.docCheck}>{row.text}</div>;
                    }

                    return <div key={i} style={UI.docPara}>{row.text}</div>;
                  })}
                </div>
              </div>

              <div style={UI.ackWrap}>
                <label style={UI.ackRow}>
                  <input
                    type="checkbox"
                    checked={!!ack[current.key]}
                    disabled={!scrolled[current.key]}
                    onChange={(e) =>
                      setAck(prev => ({ ...prev, [current.key]: e.target.checked }))
                    }
                    style={UI.checkbox}
                  />
                  <span style={UI.ackText}>
                    I’ve read and agree to the <span style={{ fontWeight: 900 }}>{current.title}</span>.
                  </span>
                </label>

                {!scrolled[current.key] ? (
                  <div style={UI.helper}>
                    Scroll to the bottom to enable the checkbox.
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <div style={UI.signatureBlock}>
              <div style={UI.sigTitle}>Electronic signature</div>
              <div style={UI.sigSub}>
                Type your legal name. This acts as your signature and is legally binding.
              </div>

              <input
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="Legal name"
                style={UI.input}
              />

              <label style={{ ...UI.ackRow, marginTop: 12 }}>
                <input
                  type="checkbox"
                  checked={finalConfirm}
                  onChange={(e) => setFinalConfirm(e.target.checked)}
                  style={UI.checkbox}
                />
                <span style={UI.ackText}>
                  I agree that this electronic signature is legally binding, and I accept the documents above.
                </span>
              </label>
            </div>
          )}

          {err ? <div style={UI.error}>{err}</div> : null}
        </div>
      </div>

      {/* Fixed bottom action bar */}
      <div style={UI.footerWrap} role="contentinfo">
        <div style={{ ...UI.footerInner, gridTemplateColumns: step === 0 ? '1fr' : '1fr 1fr' }}>
          {step > 0 && <button
            type="button"
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={footerBackDisabled}
            style={{
              ...UI.footerBtn,
              ...UI.footerBtnGhost,
              opacity: footerBackDisabled ? 0.45 : 1,
            }}
          >
            Previous
          </button>}

          {!isSignatureStep ? (
            <button
              type="button"
              onClick={() => {
                if (!canContinueDocStep()) return;
                setStep(s => Math.min(steps.length - 1, s + 1));
              }}
              disabled={footerPrimaryDisabled}
              style={{
                ...UI.footerBtn,
                ...UI.footerBtnPrimary,
                opacity: footerPrimaryDisabled ? 0.45 : 1,
              }}
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={acceptAll}
              disabled={footerPrimaryDisabled}
              style={{
                ...UI.footerBtn,
                ...UI.footerBtnPrimary,
                opacity: footerPrimaryDisabled ? 0.45 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Sign & Accept'}
            </button>
          )}
        </div>

        <div style={UI.safeAreaPad} aria-hidden="true" />
      </div>
    </div>
  );
}

const UI = {
  page: {
    minHeight: '100vh',
    background: '#fff',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    color: '#0b0b0b',
  },

  /* Header matches UpdateAccountPage */
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    background: '#fff',
    zIndex: 100,
  },
  headerLogoRow: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '0.5rem',
  },
  logoSvg: {
    width: 72,
    height: 'auto',
  },
  headerNavRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    height: 44,
    padding: '0.5rem 1rem',
  },
  logoutButton: {
    position: 'absolute',
    left: 16,
    background: 'transparent',
    color: '#0b0b0b',
    borderRadius: 9999,
    height: 28,
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    cursor: 'pointer',
    border: '1px solid #0b0b0b',
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'inherit',
  },
  pageTitle: {
    fontSize: '.95rem',
    fontWeight: 850,
    lineHeight: 1,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#0b0b0b',
    maxWidth: 'calc(100% - 160px)',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  progressContainer: {
    position: 'absolute',
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  stepCounter: {
    color: '#0b0b0b',
    fontSize: '0.9rem',
    marginRight: 10,
    fontWeight: 750,
    lineHeight: 1,
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  /* ===== Layout ===== */
  main: {
    maxWidth: 860,
    margin: '0 auto',
    padding: '16px 16px',
    paddingBottom: 'calc(86px + env(safe-area-inset-bottom))',
  },

  /* Hero */
  hero: {
    textAlign: 'center',
    padding: '0px 0 14px',
  },
  heroIconBox: {
    width: 50,
    height: 50,
    borderRadius: 12,
    background: '#f3f4f6',
    display: 'grid',
    placeItems: 'center',
    margin: '0 auto 10px',
  },
  heroTitle: {
    fontSize: '1.15rem',
    fontWeight: 900,
    color: '#0b0b0b',
    margin: '0px 0 6px',
    letterSpacing: 0.1,
  },
  heroSub: {
    fontSize: '0.92rem',
    color: '#374151',
    margin: '0 auto',
    lineHeight: 1.5,
    maxWidth: 520,
    fontWeight: 650,
  },
  heroMetaRow: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  heroChip: {
    color: '#0b0b0b',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 6,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
  },
  heroChipMuted: {
    backgroundColor: '#f3f4f6',
    color: '#374151',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 6,
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #e5e7eb',
    fontSize: 12,
  },


  /* Document */
  docBox: {
    height: 'min(58vh, 320px)',
    overflowY: 'auto',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    padding: 14,
    background: '#fff',
  },
  docInner: {
    display: 'grid',
    gap: 8,
  },
  docSpacer: {
    height: 6,
  },
  docTitle: {
    fontSize: 15,
    fontWeight: 950,
    letterSpacing: '-0.01em',
    color: '#0b0b0b',
    marginTop: 2,
  },
  docSubtitle: {
    fontSize: 12,
    fontWeight: 800,
    color: '#374151',
  },
  docMeta: {
    fontSize: 12,
    fontWeight: 800,
    color: '#374151',
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '6px 10px',
    width: 'fit-content',
  },
  docHeading: {
    marginTop: 6,
    fontSize: 13,
    fontWeight: 950,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
  },
  docPara: {
    fontSize: 13,
    fontWeight: 600,
    color: '#111827',
    lineHeight: 1.6,
  },
  docBulletRow: {
    display: 'grid',
    gridTemplateColumns: '14px 1fr',
    gap: 4,
    alignItems: 'start',
  },
  docBulletDot: {
    fontWeight: 900,
    color: '#111',
    lineHeight: '1.6',
  },
  docBulletText: {
    fontSize: 13,
    fontWeight: 650,
    color: '#111827',
    lineHeight: 1.6,
  },
  docCheck: {
    fontSize: 13,
    fontWeight: 650,
    color: '#111827',
    lineHeight: 1.6,
  },

  ackWrap: {
    marginTop: 12,
    paddingTop: 12,
  },
  ackRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
  },
  checkbox: {
    width: 16,
    height: 16,
    marginTop: 2,
    accentColor: '#111',
    flexShrink: 0,
  },
  ackText: {
    fontSize: 13,
    fontWeight: 650,
    color: '#111',
    lineHeight: 1.4,
  },
  helper: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 650,
    color: '#6b7280',
  },

  /* Signature */
  signatureBlock: {
    borderRadius: 14,
    border: '1px solid #e5e7eb',
    padding: 14,
    background: '#fff',
  },
  sigTitle: {
    fontWeight: 950,
    color: '#111',
    letterSpacing: '-0.01em',
  },
  sigSub: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: 650,
    color: '#6b7280',
    lineHeight: 1.45,
  },
  input: {
    marginTop: 12,
    width: '100%',
    height: 44,
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    padding: '0 12px',
    outline: 'none',
    fontWeight: 800,
    fontSize: 14,
    background: '#fff',
    boxSizing: 'border-box',
  },

  error: {
    marginTop: 12,
    padding: '10px 12px',
    borderRadius: 12,
    border: '1px solid rgba(185, 28, 28, 0.18)',
    background: 'rgba(185, 28, 28, 0.08)',
    color: '#7f1d1d',
    fontSize: 13,
    fontWeight: 850,
    lineHeight: 1.35,
  },

  /* Fixed footer */
  footerWrap: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 120,
    background: '#fff',
    borderTop: '1px solid #eef0f2',
  },
  footerInner: {
    maxWidth: 860,
    margin: '0 auto',
    padding: '10px 12px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  footerBtn: {
    height: 46,
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    fontWeight: 950,
    cursor: 'pointer',
    fontSize: 12,
  },
  footerBtnGhost: {
    background: '#fff',
    color: '#111',
  },
  footerBtnPrimary: {
    background: '#111',
    borderColor: '#111',
    color: '#fff',
  },
  safeAreaPad: {
    height: 'env(safe-area-inset-bottom)',
  },
};
